import React, { useState } from 'react';
import { useCompany, useTechnicians, useTeamMembers } from '../lib/hooks';
import { Globe, Settings as SettingsIcon } from 'lucide-react';
import type { ServiceType, ApplianceBrand } from '../types';
import type { Database } from '../lib/database.types';
import ServicesTab from './settings/ServicesTab';
import InvoiceSettingsTab from './settings/InvoiceSettingsTab';
import BrandsTab from './settings/BrandsTab';
import TechniciansTab from './settings/TechniciansTab';
import CustomFieldsTab from './settings/CustomFieldsTab';
import ProfileTab from './settings/ProfileTab';
import TechnicianModal from './settings/TechnicianModal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';

const Settings = () => {
  const { company, loading: companyLoading } = useCompany();
  const [selectedServices, setSelectedServices] = useState<ServiceType[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<ApplianceBrand[]>([]);
  const [activeTab, setActiveTab] = useState<'profile' | 'services' | 'brands' | 'technicians' | 'invoices' | 'custom-fields'>('profile');
  const [showAddTechnicianModal, setShowAddTechnicianModal] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState<Database['public']['Tables']['technicians']['Row'] | null>(null);
  const { addTechnician, updateTechnician } = useTechnicians();
  
  // Initialize selected services and brands when company data loads
  React.useEffect(() => {
    if (company) {
      setSelectedServices(company.services || []);
      setSelectedBrands(company.brands || []);
    }
  }, [company]);

  if (companyLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Company Settings</h1>
        <p className="text-gray-600">Manage your company's services and preferences</p>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'services'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('brands')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'brands'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Brands
            </button>
            <button
              onClick={() => setActiveTab('technicians')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'technicians'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Technicians
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoice Settings
            </button>
            <button
              onClick={() => setActiveTab('custom-fields')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'custom-fields'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Custom Fields
            </button>
          </nav>
        </div>
      </div>

      <Card className="p-6">
        {activeTab === 'profile' ? (
          <ProfileTab />
        ) : activeTab === 'services' ? (
          <ServicesTab
            selectedServices={selectedServices}
            onServiceToggle={setSelectedServices}
          />
        ) : activeTab === 'brands' ? (
          <BrandsTab
            selectedBrands={selectedBrands}
            onBrandToggle={setSelectedBrands}
          />
        ) : activeTab === 'technicians' ? (
          <TechniciansTab
            onAddTechnician={() => setShowAddTechnicianModal(true)}
            onEditTechnician={setEditingTechnician}
          />
        ) : activeTab === 'invoices' ? (
          <InvoiceSettingsTab />
        ) : activeTab === 'custom-fields' ? (
          <CustomFieldsTab />
        ) : null}
      </Card>

      {/* Add/Edit Technician Modal */}
      {(showAddTechnicianModal || editingTechnician) && (
        <TechnicianModal
          technician={editingTechnician || undefined}
          onClose={() => {
            setShowAddTechnicianModal(false);
            setEditingTechnician(null);
          }}
          onSubmit={async (technicianData) => {
            try {
              if (editingTechnician) {
                await updateTechnician(editingTechnician.id, technicianData);
                toast({
                  title: "Success",
                  description: "Technician updated successfully",
                });
              } else {
                await addTechnician(technicianData);
                toast({
                  title: "Success",
                  description: "Technician added successfully",
                });
              }
              setShowAddTechnicianModal(false);
              setEditingTechnician(null);
            } catch (err) {
              console.error('Error saving technician:', err);
              toast({
                title: "Error",
                description: err instanceof Error ? err.message : "Failed to save technician",
                variant: "destructive",
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default Settings;